import React, { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import AdelantosService from "../../services/adelantosService";
import GruposAfinidadService from "../../services/gruposAfinidadService";
import { MONTO_MAXIMO } from "./utils";

// ── Helpers de estilo ─────────────────────────────────────────────────────────
const inp =
  "w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-custom-green-ccard-1 transition-colors";
const lbl = "text-xs font-medium text-gray-500 mb-1 block";

// ── Estado inicial vacío del formulario ───────────────────────────────────────
const EMPTY_FORM = {
  id_grupo_afinidad: "",
  score_min: "",
  score_max: "",
  monto: "",
  id_plan: "",
};

/**
 * Modal compartido para crear y editar Reglas de Adelantos en Efectivo.
 *
 * Props:
 *   regla    — null → modo crear | objeto Regla → modo editar
 *   onClose  — cierra el modal sin guardar
 *   onSaved  — llamado tras POST/PUT exitoso
 */
export default function ReglaFormModal({ regla, onClose, onSaved }) {
  const isEdit = regla !== null && regla !== undefined;

  // ── Estado del formulario ─────────────────────────────────────────────────
  const [form, setForm] = useState(EMPTY_FORM);

  // ── Grupos de afinidad ────────────────────────────────────────────────────
  const [grupos, setGrupos] = useState([]);
  const [loadingGrupos, setLoadingGrupos] = useState(true);
  const [errorGrupos, setErrorGrupos] = useState(null);

  // ── Planes de pago ────────────────────────────────────────────────────────
  const [planes, setPlanes] = useState([]);
  const [loadingPlanes, setLoadingPlanes] = useState(true);
  const [errorPlanes, setErrorPlanes] = useState(null);

  // ── Guardado y validación ─────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // ── Carga de grupos y planes en paralelo al montar ────────────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      // Grupos
      try {
        const lista = await GruposAfinidadService.getAll();
        if (!cancelled) {
          setGrupos(
            lista.map((g) => ({ id_grupo_afinidad: g.id, nombre: g.nombre })),
          );
        }
      } catch {
        if (!cancelled)
          setErrorGrupos("No se pudieron cargar los grupos de afinidad.");
      } finally {
        if (!cancelled) setLoadingGrupos(false);
      }

      // Planes
      try {
        const res = await AdelantosService.getPlanes();
        if (!cancelled) {
          const raw = res?.data?.data ?? res?.data ?? res?.result ?? res ?? [];
          setPlanes(Array.isArray(raw) ? raw : []);
        }
      } catch {
        if (!cancelled) setErrorPlanes("No se pudieron cargar los planes.");
      } finally {
        if (!cancelled) setLoadingPlanes(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Pre-cargar campos en modo editar ──────────────────────────────────────
  useEffect(() => {
    if (isEdit && regla) {
      setForm({
        id_grupo_afinidad: regla.id_grupo_afinidad ?? "",
        score_min:
          regla.score_min !== null && regla.score_min !== undefined
            ? String(regla.score_min)
            : "",
        score_max:
          regla.score_max !== null && regla.score_max !== undefined
            ? String(regla.score_max)
            : "",
        monto: String(regla.monto ?? ""),
        id_plan: regla.id_plan ?? "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [regla, isEdit]);

  // ── Helper de cambio de campo ─────────────────────────────────────────────
  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setValidationError(null);
  };

  // ── Plan seleccionado (para mostrar cuotas como preview) ──────────────────
  const planSeleccionado = planes.find(
    (p) => String(p.id) === String(form.id_plan),
  );

  // ── Validación y envío ────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.id_grupo_afinidad) {
      setValidationError("Debe seleccionar un Grupo de Afinidad.");
      return;
    }

    const parsedScoreMin =
      form.score_min === "" ? null : parseInt(form.score_min, 10);
    const parsedScoreMax =
      form.score_max === "" ? null : parseInt(form.score_max, 10);

    if (
      parsedScoreMin !== null &&
      parsedScoreMax !== null &&
      parsedScoreMin > parsedScoreMax
    ) {
      setValidationError("Score Mín no puede ser mayor que Score Máx");
      return;
    }

    const parsedMonto = parseInt(form.monto, 10);
    if (!form.monto || isNaN(parsedMonto) || parsedMonto <= 0) {
      setValidationError("El monto debe ser un número positivo");
      return;
    }

    if (!form.id_plan) {
      setValidationError("Debe seleccionar un Plan de pago.");
      return;
    }

    const payload = {
      id_grupo_afinidad: Number(form.id_grupo_afinidad),
      score_min: parsedScoreMin,
      score_max: parsedScoreMax,
      monto: parsedMonto,
      id_plan: Number(form.id_plan),
    };

    setValidationError(null);
    setSaving(true);
    try {
      if (isEdit) {
        await AdelantosService.updateRegla(regla.id, {
          ...payload,
          activo: regla.activo ? 1 : 0,
        });
      } else {
        await AdelantosService.createRegla(payload);
      }
      Swal.fire({
        icon: "success",
        title: isEdit ? "Regla actualizada" : "Regla creada",
        position: "top-end",
        toast: true,
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
      onSaved();
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.resp || "No se pudo guardar la regla.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Loading global (grupos + planes) ─────────────────────────────────────
  const isLoading = loadingGrupos || loadingPlanes;
  const hasError = errorGrupos || errorPlanes;
  const confirmDisabled = isLoading || !!hasError || saving;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">
            {isEdit ? "Editar regla" : "Nueva regla"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Cerrar modal"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="h-7 w-7 rounded-full border-4 border-custom-green-ccard-1 border-t-transparent animate-spin" />
              <span className="ml-3 text-sm text-gray-500">
                Cargando datos…
              </span>
            </div>
          )}

          {!isLoading && hasError && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 space-y-1">
              {errorGrupos && <p>{errorGrupos}</p>}
              {errorPlanes && <p>{errorPlanes}</p>}
            </div>
          )}

          {!isLoading && !hasError && (
            <form id="regla-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Grupo de Afinidad */}
              <div>
                <label className={lbl}>Grupo de Afinidad *</label>
                <select
                  className={inp}
                  value={form.id_grupo_afinidad}
                  onChange={(e) =>
                    set(
                      "id_grupo_afinidad",
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                >
                  <option value="">— Seleccionar —</option>
                  {grupos.map((g) => (
                    <option
                      key={g.id_grupo_afinidad}
                      value={g.id_grupo_afinidad}
                    >
                      {g.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Score Mín / Score Máx */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Score Mín (opcional)</label>
                  <input
                    className={inp}
                    type="number"
                    min="0"
                    max="999"
                    value={form.score_min}
                    onChange={(e) => set("score_min", e.target.value)}
                    placeholder="0–999"
                  />
                </div>
                <div>
                  <label className={lbl}>Score Máx (opcional)</label>
                  <input
                    className={inp}
                    type="number"
                    min="0"
                    max="999"
                    value={form.score_max}
                    onChange={(e) => set("score_max", e.target.value)}
                    placeholder="0–999"
                  />
                </div>
              </div>

              {/* Monto */}
              <div>
                <label className={lbl}>Monto *</label>
                <input
                  className={inp}
                  type="number"
                  min="1"
                  max={MONTO_MAXIMO}
                  value={form.monto}
                  onChange={(e) => set("monto", e.target.value)}
                  placeholder="Ej: 50000"
                />
              </div>

              {/* Plan de pago */}
              <div>
                <label className={lbl}>Plan de pago *</label>
                <select
                  className={inp}
                  value={form.id_plan}
                  onChange={(e) =>
                    set(
                      "id_plan",
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                >
                  <option value="">— Seleccionar plan —</option>
                  {planes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                    </option>
                  ))}
                </select>
                {/* Preview cuotas del plan seleccionado */}
                {planSeleccionado &&
                  Array.isArray(planSeleccionado.cuotas) &&
                  planSeleccionado.cuotas.length > 0 && (
                    <p className="mt-1.5 text-xs text-gray-400">
                      Cuotas disponibles:{" "}
                      <span className="font-medium text-gray-600">
                        {planSeleccionado.cuotas
                          .map((c) =>
                            typeof c === "object" ? (c.cuota ?? c.id) : c,
                          )
                          .join(", ")}
                      </span>
                    </p>
                  )}
              </div>

              {/* Error de validación */}
              {validationError && (
                <div className="rounded-xl bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-800">
                  {validationError}
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="regla-form"
            disabled={confirmDisabled}
            className="px-5 py-2 rounded-xl bg-custom-green-ccard-1 text-white text-sm font-medium hover:bg-custom-green-ccard-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear regla"}
          </button>
        </div>
      </div>
    </div>
  );
}
